import { gameState } from '../state/gameState.js';
import { clockTime } from '../state/gameClock.js';
import { serverNow } from '../state/serverClock.mjs';

// Live hour hand for the wall clock (furniture `zegar`). The dial is a plain placement
// sprite; this adds a second sprite pivoting on the dial's centre and re-syncs it every
// frame (position, scale, angle), so it follows the dial through moves and dies with it.
// Works for owner scenes and read-only visit renders alike — the cycle anchor is global.

const HAND_TEX = 'furniture_zegar_hand';

// Measured on the source art, as fractions of the (square) texture size.
const DIAL_CENTER = { x: 624 / 1254, y: 669 / 1254 };      // gold pin on clock.png
const HAND_PIVOT = { x: 165.5 / 1254, y: 1074.5 / 1254 };  // ring hole on clock_hand.png
const HAND_ART_ANGLE_DEG = -44.4;   // direction the hand is drawn pointing (0° = right, screen coords)
const HAND_TEX_LEN = 1477 / 1254;   // pivot→tip length in texture widths
const HAND_LEN = 0.27;              // desired pivot→tip length as a fraction of the dial's display width

// Mirrors backend CYCLE_EPOCH_ISO — used only if the state has not arrived yet.
const FALLBACK_ANCHOR_MS = Date.UTC(2026, 0, 1);

export function isClock(p) {
  return p?.type === 'furniture' && p?.item === 'zegar';
}

// Current in-game clock time honoring the dev freeze (T/R hotkeys).
export function liveClockTime(now = serverNow()) {
  const iso = gameState.raw?.time?.cycle_started_at_iso;
  const anchor = iso ? new Date(iso).getTime() : FALLBACK_ANCHOR_MS;
  return clockTime(now, anchor, gameState._devPhase ?? null);
}

// opts.fixedHours: show a static time (e.g. 2 → "2:00") instead of the live game clock —
// used by the furniture shop icon, where a spinning hand would be a distraction.
export function attachClockHand(scene, dial, placement, { fixedHours = null } = {}) {
  if (!isClock(placement) || !scene.textures.exists(HAND_TEX)) return null;
  const hourAngle = fixedHours === null ? null : (fixedHours % 12) * 30;
  const hand = scene.add.image(0, 0, HAND_TEX)
    .setOrigin(HAND_PIVOT.x, HAND_PIVOT.y)
    .setDepth(dial.depth + 1);

  const sync = () => {
    const w = dial.displayWidth, h = dial.displayHeight;
    hand.setPosition(dial.x - w / 2 + w * DIAL_CENTER.x, dial.y - h / 2 + h * DIAL_CENTER.y);
    hand.setScale((w * HAND_LEN) / (hand.width * HAND_TEX_LEN));
    hand.setAngle((hourAngle ?? liveClockTime().hourAngleDeg) - 90 - HAND_ART_ANGLE_DEG);
    hand.setVisible(dial.visible);
    hand.setDepth(dial.depth + 1);   // dial depth is y-sorted per frame
  };
  sync();
  scene.events.on('update', sync);
  dial.once('destroy', () => {
    scene.events.off('update', sync);
    hand.destroy();
  });
  return hand;
}
